import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useGlobalContext } from "../../context/global";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  MenuItem,
  Paper,
  Popover,
  Select,
  Skeleton,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import HistoryIcon from "@mui/icons-material/History";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import axios from "axios";
import { toast } from "react-toastify";
import { EXECUTE_RULE_TYPE, HEADING_TITLE_COLOR, RULE_TYPE } from "./lib/constants";
import {
  ABO_COLORS,
  sectionCardSx,
  sectionSubtitleSx,
  tableHeadCellSx,
} from "./lib/campaignAboStyles";
import {
  formatValidationIssueMessage,
  getCampaignCount,
  getModeLabel,
  getRuleCampaignIds,
  getRuleId,
  getRuleLastRunTime,
  getRuleName,
  getRuleUpdatedAt,
  getScheduleRange,
} from "./lib/helpers";
import { fetchCampaignScheduleConflictValidation } from "./lib/validation";
import CampaignAboLogsModal from "./components/CampaignAboLogsModal";
import CustomPagination from "../../components/CustomPagination/CustomPagination";
import useUserDetails from "../../helper/userData/useUserDetails";

const SHORT_MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

function parseScheduleDateString(value) {
  const s = String(value ?? "").trim();
  if (!s) return null;
  const isoDay = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoDay) {
    const y = parseInt(isoDay[1], 10);
    const m = parseInt(isoDay[2], 10) - 1;
    const d = parseInt(isoDay[3], 10);
    const dt = new Date(y, m, d);
    return Number.isNaN(dt.getTime()) ? null : dt;
  }
  const dt = new Date(s);
  return Number.isNaN(dt.getTime()) ? null : dt;
}

function formatScheduleDateForList(value) {
  const dt = parseScheduleDateString(value);
  if (!dt) return null;
  const mon = SHORT_MONTHS[dt.getMonth()];
  return `${dt.getDate()} ${mon}, ${dt.getFullYear()}`;
}

const SKELETON_ROW_COUNT = 6;
const LIST_COLUMNS = ["Status", "Rule Name", "Mode", "Campaigns", "Updated At", "Last Run Time", "Date Range", "Action"];
const PAGE_SIZE_OPTIONS = [50, 100, 200, 300, 500];

function CampaignAboList() {
  const navigate = useNavigate();
  const dashboardSideBarVisibiltyState = useSelector(
    (state) => state.dashboardData.dashboardSideBarVisibiltyState
  );
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageLimit, setPageLimit] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const [togglingId, setTogglingId] = useState(null);
  const [logsModalRuleName, setLogsModalRuleName] = useState(null);
  const [triggerConfirmRule, setTriggerConfirmRule] = useState(null);
  const [triggeringId, setTriggeringId] = useState(null);
  const [activatePopover, setActivatePopover] = useState({
    anchorEl: null,
    rule: null,
  });
  const [activateValidation, setActivateValidation] = useState({
    loading: false,
    valid: false,
    issues: [],
  });

  const { clientId, selectedPlatform } = useGlobalContext();
  const userPlatform = selectedPlatform ?? "amazon";
  const [userDetails] = useUserDetails();
  const email = userDetails?.user?.email ?? "";

  const loadRulesFromApi = useCallback(async () => {
    if (!clientId) {
      setRules([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_ADS_API}/client/${clientId}/automation-rule-set?source=${userPlatform}&type=${RULE_TYPE}&order_by=name&sorting_order=asc&page=${currentPage - 1}&limit=${pageLimit}`
      );
      const list = Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : [];
      const totalFromApi = data?.total_count ?? null;
      setRules(list.filter((rule) => rule?.type === RULE_TYPE));
      if (totalFromApi !== null && totalFromApi !== undefined) {
        setTotalCount(totalFromApi);
      } else if (list.length < pageLimit) {
        setTotalCount((currentPage - 1) * pageLimit + list.length);
      } else {
        setTotalCount((currentPage - 1) * pageLimit + list.length + 1);
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message ??
          error?.response?.data?.error ??
          error?.message ??
          "Failed to load Campaign ABO rules"
      );
      setRules([]);
    } finally {
      setLoading(false);
    }
  }, [clientId, userPlatform, currentPage, pageLimit]);

  useEffect(() => {
    loadRulesFromApi();
  }, [loadRulesFromApi]);

  useEffect(() => {
    setCurrentPage(1);
  }, [clientId, userPlatform]);

  const sortedRules = useMemo(
    () =>
      [...rules].sort(
        (a, b) =>
          new Date(getRuleUpdatedAt(b) || 0).getTime() -
          new Date(getRuleUpdatedAt(a) || 0).getTime()
      ),
    [rules]
  );

  const getDateRangeText = (rule) => {
    const { start, end } = getScheduleRange(rule);
    if (!start && !end) return "No date range";
    if (!start || !end) return start || end || "-";
    const a = formatScheduleDateForList(start);
    const b = formatScheduleDateForList(end);
    if (!a || !b) return `${start} to ${end}`;
    return `${a} to ${b}`;
  };

  const closeActivatePopover = useCallback(() => {
    setActivatePopover({ anchorEl: null, rule: null });
    setActivateValidation({ loading: false, valid: false, issues: [] });
  }, []);

  const patchRuleStatus = useCallback(
    async (rule, nextStatus) => {
      const ruleId = getRuleId(rule);
      if (!ruleId || !clientId) return false;
      setTogglingId(ruleId);
      try {
        const { data: res } = await axios.patch(
          `${process.env.REACT_APP_ADS_API}/client/${clientId}/automation-rule-set/${ruleId}?source=${userPlatform}&type=${RULE_TYPE}`,
          { status: nextStatus }
        );
        if (res?.success === false) {
          throw new Error("Status update failed");
        }
        setRules((prev) =>
          prev.map((r) =>
            getRuleId(r) === ruleId
              ? { ...r, status: nextStatus, updated_at: new Date().toISOString() }
              : r
          )
        );
        toast.success(`"${getRuleName(rule)}" is now ${nextStatus}.`);
        return true;
      } catch (error) {
        toast.error(
          error?.response?.data?.message ??
            error?.response?.data?.error ??
            error?.message ??
            "Failed to update rule status"
        );
        return false;
      } finally {
        setTogglingId(null);
      }
    },
    [clientId, userPlatform]
  );

  const runActivateValidation = useCallback(
    async (rule) => {
      const { start, end } = getScheduleRange(rule);
      setActivateValidation({ loading: true, valid: false, issues: [] });
      const result = await fetchCampaignScheduleConflictValidation({
        clientId,
        userPlatform,
        campaignIds: getRuleCampaignIds(rule),
        scheduleStartDate: start,
        scheduleEndDate: end,
        excludeRuleSetId: getRuleId(rule),
      });
      setActivateValidation({
        loading: false,
        valid: result.valid,
        issues: result.issues,
      });
    },
    [clientId, userPlatform]
  );

  useEffect(() => {
    if (!activatePopover.rule) return undefined;
    runActivateValidation(activatePopover.rule);
    return undefined;
  }, [activatePopover.rule, runActivateValidation]);

  const handleStatusSwitch = (event, rule) => {
    if (rule.status === "Active") {
      patchRuleStatus(rule, "Paused");
      return;
    }
    const anchorEl =
      event.currentTarget.closest("[data-abo-status-anchor]") ?? event.currentTarget;
    setActivatePopover({ anchorEl, rule });
  };

  const handleConfirmActivate = async () => {
    const rule = activatePopover.rule;
    if (!rule || activateValidation.loading || !activateValidation.valid) return;
    closeActivatePopover();
    await patchRuleStatus(rule, "Active");
  };

  const handleConfirmTrigger = async () => {
    const rule = triggerConfirmRule;
    const ruleId = getRuleId(rule);
    if (!ruleId || !clientId) return;
    setTriggeringId(ruleId);
    try {
      const params = new URLSearchParams({
        type: EXECUTE_RULE_TYPE,
        client_id: clientId,
        TRIGGER_BY: email,
      });
      await axios.post(
        `${process.env.REACT_APP_ADS_API}/client/${clientId}/automation_rule_set/${ruleId}:execute-automation-rule-set?${params.toString()}`
      );
      toast.success(`"${getRuleName(rule)}" was triggered.`);
      setTriggerConfirmRule(null);
      loadRulesFromApi();
    } catch (error) {
      toast.error(
        error?.response?.data?.message ??
          error?.response?.data?.error ??
          error?.message ??
          "Some error occurred while executing the rule"
      );
    } finally {
      setTriggeringId(null);
    }
  };

  const activateRuleName = activatePopover.rule ? getRuleName(activatePopover.rule) : "";
  const totalPages = Math.max(1, Math.ceil(totalCount / pageLimit));
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };
  const handlePageLimitChange = (event) => {
    setPageLimit(Number(event.target.value));
    setCurrentPage(1);
  };

  return (
    <div
      className={
        dashboardSideBarVisibiltyState
          ? "main-content-container-onopen-mode"
          : "main-content-container-oncolapse-mode"
      }
      id="main-box"
    >
      <Box sx={{ py: 0, px: { xs: 2, md: 3 }, minHeight: "100%" }}>
        <Box sx={{ maxWidth: 1200, mx: "auto" }}>
        <Box sx={{ py: 0, mb: 4 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, color: HEADING_TITLE_COLOR, fontSize: "1.375rem", mb: 0.5 }}>
                Campaign ABO Rules
              </Typography>
              <Typography variant="body2" sx={{ ...sectionSubtitleSx }}>
                Automated budget optimization by time slots and schedule.
              </Typography>
            </Box>
            <Button
              size="small"
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate("/campaign-abo-list/campaign-abo-create")}
              disabled={!clientId}
            >
              Create Rule
            </Button>
          </Stack>
        </Box>

        {!clientId ? (
          <Typography color="text.secondary">Select a client to load Campaign ABO rules.</Typography>
        ) : (
          <>
            <TableContainer component={Paper} variant="outlined" sx={{ ...sectionCardSx, overflow: "hidden" }}>
              <Table>
                <TableHead>
                  <TableRow>
                    {LIST_COLUMNS.map((label) => (
                      <TableCell key={label} align="center" sx={tableHeadCellSx}>
                        {label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    Array.from({ length: SKELETON_ROW_COUNT }, (_, i) => (
                      <TableRow key={`sk-${i}`}>
                        {Array.from({ length: LIST_COLUMNS.length }).map((__, j) => (
                          <TableCell key={j} align="center">
                            <Skeleton
                              variant="text"
                              width={j === LIST_COLUMNS.length - 1 ? 36 : "80%"}
                              height={22}
                              sx={{ mx: "auto" }}
                            />
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  ) : sortedRules.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={LIST_COLUMNS.length} align="center" sx={{ height: 100 }}>
                        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
                          <Typography color="text.secondary">
                            No Campaign ABO rules found. Click Create Rule to add one.
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : (
                    sortedRules.map((rule) => {
                      const oid = getRuleId(rule);
                      return (
                        <TableRow key={oid} hover>
                          <TableCell align="center">
                            <Stack
                              data-abo-status-anchor
                              direction="row"
                              alignItems="center"
                              justifyContent="center"
                              sx={{ minHeight: 38, minWidth: 58 }}
                            >
                              {togglingId === oid ? (
                                <CircularProgress size={22} thickness={5} />
                              ) : (
                                <Switch
                                  size="small"
                                  checked={rule.status === "Active"}
                                  disabled={!oid}
                                  onChange={(e) => handleStatusSwitch(e, rule)}
                                />
                              )}
                            </Stack>
                          </TableCell>
                          <TableCell align="center" sx={{ fontWeight: 600, color: ABO_COLORS.title, fontSize: "0.875rem" }}>
                            {getRuleName(rule)}
                          </TableCell>
                          <TableCell align="center" sx={{ fontSize: "0.875rem", color: ABO_COLORS.body }}>
                            {getModeLabel(rule)}
                          </TableCell>
                          <TableCell align="center" sx={{ fontSize: "0.875rem", color: ABO_COLORS.body }}>
                            {getCampaignCount(rule)}
                          </TableCell>
                          <TableCell align="center" sx={{ fontSize: "0.875rem", color: ABO_COLORS.body }}>
                            {getRuleUpdatedAt(rule)
                              ? new Date(getRuleUpdatedAt(rule)).toLocaleString()
                              : "-"}
                          </TableCell>
                          <TableCell align="center" sx={{ fontSize: "0.875rem", color: ABO_COLORS.body }}>
                            {getRuleLastRunTime(rule)
                              ? new Date(getRuleLastRunTime(rule)).toLocaleString()
                              : "-"}
                          </TableCell>
                          <TableCell align="center" sx={{ fontSize: "0.875rem", color: ABO_COLORS.body }}>
                            {getDateRangeText(rule)}
                          </TableCell>
                          <TableCell align="center">
                            <Stack direction="row" justifyContent="center" spacing={0.5}>
                              <Tooltip title="Run now">
                                <span>
                                  <IconButton
                                    disabled={!oid || triggeringId === oid}
                                    onClick={() => setTriggerConfirmRule(rule)}
                                    size="small"
                                    sx={{ color: ABO_COLORS.muted }}
                                  >
                                    {triggeringId === oid ? (
                                      <CircularProgress size={16} thickness={5} />
                                    ) : (
                                      <PlayCircleOutlineIcon fontSize="small" />
                                    )}
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip title="View logs">
                                <span>
                                  <IconButton
                                    disabled={!getRuleName(rule)}
                                    onClick={() => setLogsModalRuleName(getRuleName(rule))}
                                    size="small"
                                    sx={{ color: ABO_COLORS.muted }}
                                  >
                                    <HistoryIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip title="Edit rule">
                                <span>
                                  <IconButton
                                    disabled={!oid}
                                    onClick={() =>
                                      navigate(`/campaign-abo-list/campaign-abo-create?id=${oid}`)
                                    }
                                    color="primary"
                                    size="small"
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {!loading && (totalCount > 0 || currentPage > 1) && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                  py: 2,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    color: "#4f5366",
                  }}
                >
                  <Typography variant="body2" sx={{ fontSize: "12px" }}>
                    Page Size
                  </Typography>
                  <FormControl size="small" sx={{ minWidth: 80 }}>
                    <Select
                      value={pageLimit}
                      onChange={handlePageLimitChange}
                      disabled={loading}
                      sx={{
                        height: 36,
                        fontSize: "12px",
                        color: "#333333",
                      }}
                      MenuProps={{ disableScrollLock: true }}
                    >
                      {PAGE_SIZE_OPTIONS.map((option) => (
                        <MenuItem
                          key={option}
                          value={option}
                          sx={{ fontSize: "12px" }}
                        >
                          {option}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                <CustomPagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                  lazyLoading={loading}
                  totalItems={totalCount}
                  itemsPerPage={pageLimit}
                  hidePagination={!totalCount}
                />
              </Box>
            )}
          </>
        )}
        </Box>
      </Box>

      <CampaignAboLogsModal
        open={Boolean(logsModalRuleName)}
        onClose={() => setLogsModalRuleName(null)}
        ruleName={logsModalRuleName}
        clientId={clientId}
        source={userPlatform}
      />

      <Popover
        open={Boolean(activatePopover.anchorEl)}
        anchorEl={activatePopover.anchorEl}
        onClose={closeActivatePopover}
        disableRestoreFocus
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
        slotProps={{
          paper: {
            sx: {
              p: 2,
              width: 360,
              maxWidth: "calc(100vw - 32px)",
              borderRadius: 2,
              border: `1px solid ${ABO_COLORS.border}`,
              boxShadow: "0 8px 24px rgba(15, 23, 42, 0.12)",
            },
          },
        }}
      >
        <Typography sx={{ fontWeight: 700, fontSize: "0.9375rem", color: ABO_COLORS.title, mb: 0.5 }}>
          Activate rule?
        </Typography>
        <Typography sx={{ fontSize: "0.8125rem", color: ABO_COLORS.muted, mb: 1.5 }}>
          {activateRuleName}
        </Typography>

        {activateValidation.loading ? (
          <Stack direction="row" alignItems="center" spacing={1} sx={{ py: 1 }}>
            <CircularProgress size={20} />
            <Typography sx={{ fontSize: "0.8125rem", color: ABO_COLORS.muted }}>
              Validating schedule…
            </Typography>
          </Stack>
        ) : null}

        {!activateValidation.loading && activateValidation.issues.length > 0 ? (
          <Box role="alert" sx={{ mb: 1.5 }}>
            {activateValidation.issues.map((issue, index) => (
              <Typography
                key={`activate-issue-${index}`}
                sx={{
                  fontSize: "0.8125rem",
                  fontWeight: 500,
                  color: "error.main",
                  lineHeight: 1.5,
                  mb: index < activateValidation.issues.length - 1 ? 0.5 : 0,
                }}
              >
                {formatValidationIssueMessage(issue?.message)}
              </Typography>
            ))}
          </Box>
        ) : null}

        {!activateValidation.loading && activateValidation.valid ? (
          <Typography sx={{ fontSize: "0.8125rem", color: ABO_COLORS.body, mb: 1.5 }}>
            No schedule conflicts found. You can activate this rule.
          </Typography>
        ) : null}

        <Stack direction="row" justifyContent="flex-end" spacing={1}>
          <Button
            size="small"
            variant="outlined"
            onClick={closeActivatePopover}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={handleConfirmActivate}
            disabled={
              activateValidation.loading ||
              !activateValidation.valid ||
              togglingId === getRuleId(activatePopover.rule)
            }
            sx={{ textTransform: "none", fontWeight: 600, boxShadow: "none" }}
          >
            Activate
          </Button>
        </Stack>
      </Popover>

      <Dialog
        open={Boolean(triggerConfirmRule)}
        onClose={() => {
          if (!triggeringId) setTriggerConfirmRule(null);
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700, color: ABO_COLORS.title }}>
          Run this rule now?
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: "0.875rem", color: ABO_COLORS.body }}>
            {triggerConfirmRule ? getRuleName(triggerConfirmRule) : ""} will be triggered
            manually.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 0 }}>
          <Button
            size="small"
            variant="outlined"
            disabled={Boolean(triggeringId)}
            onClick={() => setTriggerConfirmRule(null)}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            disabled={Boolean(triggeringId) || !triggerConfirmRule}
            onClick={handleConfirmTrigger}
            sx={{ textTransform: "none", fontWeight: 600, boxShadow: "none" }}
          >
            {triggeringId ? "Running…" : "Run"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default CampaignAboList;
